
import { useState } from 'react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuoteModal({ isOpen, onClose }: QuoteModalProps) {
  const [formData, setFormData] = useState({
    volume_heures: '',
    nombre_dossiers: '',
    secteur_activite: '',
    services: [] as string[],
    nom_cabinet: '',
    nom_contact: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const services = [
    'Tenue de comptabilité',
    'Établissement des comptes',
    'Déclarations fiscales',
    'Paie et social',
    'Reporting et analyse',
    'Support dédié'
  ];

  const secteurs = [
    'Commerce de détail',
    'Services aux entreprises',
    'BTP et construction',
    'Restauration et hôtellerie',
    'Professions libérales',
    'Industrie manufacturière',
    'Transport et logistique',
    'Immobilier',
    'Santé et social',
    'Technologies et informatique',
    'Agriculture',
    'Autre'
  ];

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('volume_heures', formData.volume_heures);
      formDataToSubmit.append('nombre_dossiers', formData.nombre_dossiers);
      formDataToSubmit.append('secteur_activite', formData.secteur_activite);
      formDataToSubmit.append('services', formData.services.join(', '));
      formDataToSubmit.append('nom_cabinet', formData.nom_cabinet);
      formDataToSubmit.append('nom_contact', formData.nom_contact);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('telephone', formData.telephone);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d303vpuld3g0jopqacog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitMessage('Votre demande de devis a été envoyée avec succès ! Nous vous recontacterons sous 24h.');
        setFormData({
          volume_heures: '',
          nombre_dossiers: '',
          secteur_activite: '',
          services: [],
          nom_cabinet: '',
          nom_contact: '',
          email: '',
          telephone: '',
          message: ''
        });
      } else {
        setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
    
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Demande de devis personnalisé</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {submitMessage && (
            <div className={`mb-6 p-4 rounded-lg ${
              submitMessage.includes('succès') 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="quote-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume d'heures estimé par mois *
                </label>
                <select
                  name="volume_heures"
                  value={formData.volume_heures}
                  onChange={(e) => setFormData(prev => ({ ...prev, volume_heures: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez</option>
                  <option value="Moins de 50h">Moins de 50h</option>
                  <option value="50-100h">50-100h</option>
                  <option value="100-200h">100-200h</option>
                  <option value="200-300h">200-300h</option>
                  <option value="Plus de 300h">Plus de 300h</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de dossiers *
                </label>
                <select
                  name="nombre_dossiers"
                  value={formData.nombre_dossiers}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_dossiers: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez</option>
                  <option value="1-10 dossiers">1-10 dossiers</option>
                  <option value="11-25 dossiers">11-25 dossiers</option>
                  <option value="26-50 dossiers">26-50 dossiers</option>
                  <option value="51-100 dossiers">51-100 dossiers</option>
                  <option value="Plus de 100 dossiers">Plus de 100 dossiers</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secteur d'activité principal *
              </label>
              <select
                name="secteur_activite"
                value={formData.secteur_activite}
                onChange={(e) => setFormData(prev => ({ ...prev, secteur_activite: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                required
              >
                <option value="">Sélectionnez un secteur</option>
                {secteurs.map((secteur) => (
                  <option key={secteur} value={secteur}>{secteur}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Services demandés * (plusieurs choix possibles)
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {services.map((service) => (
                  <label key={service} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleServiceChange(service)}
                      className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du cabinet *
                </label>
                <input
                  type="text"
                  name="nom_cabinet"
                  value={formData.nom_cabinet}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom_cabinet: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du contact *
                </label>
                <input
                  type="text"
                  name="nom_contact"
                  value={formData.nom_contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom_contact: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message complémentaire
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Précisez vos besoins spécifiques..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caractères</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting || formData.services.length === 0}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
